import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, ChevronRight, Loader2, Search } from 'lucide-react';
import { listStudios, studioLabel } from './adminImportApi';
import { loadDraft } from './importDrafts';
import DataImportWizard from './DataImportWizard';
import '../../../styles/ProductImport.css';

const searchText = (studio) =>
  [studio.companyName, studio.ownerName, studio.email].filter(Boolean).join(' ').toLowerCase();

// Support Center tab: pick a studio, then import its data (products and
// services, clients, vendors, projects, tax rates, rooms, time) into it.
export default function AdminProductImport() {
  const [studios, setStudios] = useState(null);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [account, setAccount] = useState(null);

  const load = useCallback(async () => {
    setError('');
    try {
      setStudios(await listStudios());
    } catch (err) {
      console.error('Failed to load studios', err);
      setError(err?.message || 'Failed to load studios');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const matches = useMemo(() => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return (studios || []).filter((studio) => {
      const text = searchText(studio);
      return words.every((word) => text.includes(word));
    });
  }, [studios, query]);

  if (account) {
    return (
      <DataImportWizard
        key={account.userId}
        account={account}
        onChangeAccount={() => setAccount(null)}
      />
    );
  }

  let list;
  if (error) {
    list = (
      <div className="pi-banner pi-banner--error" role="alert">
        <AlertCircle size={18} aria-hidden="true" />
        <span>{error}</span>
        <div className="pi-banner-actions">
          <button type="button" className="pi-btn pi-btn--outline" onClick={load}>
            Try again
          </button>
        </div>
      </div>
    );
  } else if (!studios) {
    list = (
      <p className="pi-empty">
        <Loader2 size={16} className="pi-spin" aria-hidden="true" /> Loading studios…
      </p>
    );
  } else if (!matches.length) {
    list = (
      <p className="pi-empty">
        {studios.length ? `No studios match “${query.trim()}”.` : 'No studio accounts yet.'}
      </p>
    );
  } else {
    list = (
      <ul className="pi-studio-list">
        {matches.map((studio) => {
          const name = studioLabel(studio);
          const details = [studio.ownerName, studio.email].filter((d) => d && d !== name);
          return (
            <li key={studio.userId}>
              <button type="button" className="pi-studio" onClick={() => setAccount(studio)}>
                <span className="pi-studio-text">
                  <span className="pi-studio-name">{name}</span>
                  {details.length > 0 && (
                    <span className="pi-studio-meta">{details.join(' · ')}</span>
                  )}
                </span>
                {loadDraft(studio.userId) && (
                  <span className="pi-chip pi-chip--info">Draft saved</span>
                )}
                <ChevronRight size={18} aria-hidden="true" />
              </button>
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <div className="pi-page">
      <header className="pi-header">
        <h2>Data Import</h2>
        <p>
          Choose the studio whose export you&apos;re importing. Clients, projects, vendors, products
          and the rest are added to that studio&apos;s account.
        </p>
      </header>

      <section className="pi-card pi-picker">
        <label className="pi-search">
          <Search size={16} aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by studio, owner or email"
            aria-label="Search studios"
          />
        </label>
        {list}
      </section>
    </div>
  );
}
